// Ported from src/components/GroupSettingsDeviceList/GroupSettingsDeviceList.js on 2026-10-05.
import type { MouseEvent } from 'react';
import type { BaseTableColumnDef } from 'components/Tables/baseTablePreferences';
import type {
  GroupSettingsDevice,
  GroupSettingsDeviceListProps,
} from './groupSettingsDeviceList.types';
import NodeStatus from 'features/Integrations/components/NodeStatus/NodeStatus';
import BaseTable from 'components/Tables/BaseTable';
import Hls from 'hls.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IoVolumeHigh, IoVolumeMute } from 'react-icons/io5';
import styles from './assets/GroupSettingsDeviceList.module.css';

const canDeviceListenLive = (device: GroupSettingsDevice, hideUnavailableLiveAudio: boolean) =>
  Boolean(device?.camera) && (!hideUnavailableLiveAudio || Number(device?.is_simulated) !== 1);

/**
 * @param {{devices: object[], onSelect: (deviceId: number)}}
 */
export default function GroupSettingsDeviceList({
  devices = [],
  highlightIds = [],
  onSelect = () => {},
  tableLayout = false,
  enableTableOptions = false,
  hideUnavailableLiveAudio = false,
  compactTable = false,
  tablePreferenceKey = 'group-settings-devices',
}: GroupSettingsDeviceListProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  const [playing, setPlaying] = useState<number | null>(null);
  const [errMsg, setErrorMsg] = useState<{ [deviceId: number]: string } | string>({});

  const onListen = (evt: MouseEvent<HTMLButtonElement>, deviceId: number) => {
    evt.stopPropagation();
    setErrorMsg({});

    if (hlsRef.current) hlsRef.current.destroy();

    const url = `${process.env.PUBLIC_API_URL}/audio_v2/${deviceId}/hls/index.m3u8`;

    const hls = new Hls();
    hlsRef.current = hls;
    hls.loadSource(url);
    hls.attachMedia(audioRef.current);
    hls.on(Hls.Events.ERROR, (_event, data) => {
      console.error('Error in DeviceControls:Hls:', data);
      setErrorMsg({ [deviceId]: data.type });
    });

    audioRef.current?.addEventListener('playing', clearErrorMsg);
    audioRef.current?.addEventListener('waiting', buffering);
    audioRef.current?.addEventListener('stalled', buffering);

    setPlaying(deviceId);
  };

  const onStopListen = (evt: MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setErrorMsg({});

    hlsRef.current?.destroy();
    hlsRef.current = null;

    audioRef.current?.pause();
    audioRef.current?.removeAttribute('src');
    audioRef.current?.load();
    audioRef.current?.removeEventListener('playing', clearErrorMsg);
    audioRef.current?.removeEventListener('waiting', buffering);
    audioRef.current?.removeEventListener('stalled', buffering);

    setPlaying(null);
  };

  const buffering = () => setErrorMsg('Loading');

  const clearErrorMsg = () => setErrorMsg('');

  useEffect(
    () => () => {
      hlsRef.current?.destroy();
      audioRef.current?.pause();
    },
    [],
  );

  const hasListenLiveDevices = useMemo(
    () => devices.some((device) => canDeviceListenLive(device, hideUnavailableLiveAudio)),
    [devices, hideUnavailableLiveAudio],
  );

  const tableColumns = useMemo(() => {
    const columns: BaseTableColumnDef<GroupSettingsDevice>[] = [
      {
        accessorKey: 'alias',
        header: 'Device Name',
        minSize: compactTable ? 120 : 180,
        size: compactTable ? 180 : undefined,
        grow: 2,
        Cell: ({ row }) => (
          <span className="text-truncate d-inline-block w-100" title={row.original.alias}>
            {row.original.alias || row.original.name || 'Unnamed device'}
          </span>
        ),
      },
      {
        accessorKey: 'node_id',
        header: 'Node ID',
        minSize: compactTable ? 72 : 100,
        size: compactTable ? 84 : 120,
        grow: 1,
      },
      {
        id: 'status',
        header: 'Status',
        minSize: compactTable ? 100 : 130,
        size: compactTable ? 112 : 150,
        grow: 1,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex justify-content-start w-100 pe-2">
            <NodeStatus device={row.original} />
          </div>
        ),
      },
    ];

    if (hasListenLiveDevices) {
      columns.splice(2, 0, {
        id: 'listenLive',
        header: 'Listen Live',
        minSize: compactTable ? 84 : 110,
        size: compactTable ? 96 : 120,
        grow: 1,
        enableSorting: false,
        Cell: ({ row }) => {
          const device = row.original;
          if (!canDeviceListenLive(device, hideUnavailableLiveAudio)) return null;

          return playing === device.id ? (
            <button
              type="button"
              className={styles['sound-button']}
              onClick={onStopListen}
              aria-label={`Stop listening to ${device.alias || device.name || 'device'}`}
            >
              <IoVolumeHigh size={20} />
            </button>
          ) : (
            <button
              type="button"
              className={styles['sound-button']}
              onClick={(event) => onListen(event, device.id)}
              aria-label={`Listen to ${device.alias || device.name || 'device'}`}
            >
              <IoVolumeMute size={20} />
            </button>
          );
        },
      });
    }

    return columns;
  }, [compactTable, hasListenLiveDevices, hideUnavailableLiveAudio, playing]);

  if (tableLayout) {
    return (
      <div className="d-flex flex-column h-100 min-h-0 rounded-3 bg-primary p-2">
        {!enableTableOptions && <div className="fw-semibold pb-2">Devices in Group</div>}
        <BaseTable
          columns={tableColumns}
          rows={devices}
          onRowClick={(device) => onSelect(device.id)}
          enableRowVirtualization={false}
          layoutMode={compactTable ? 'grid' : undefined}
          enableTableOptions={enableTableOptions}
          tableOptionsStart={<div className="fw-semibold">Devices in Group</div>}
          tablePreferenceKey={tablePreferenceKey}
          customEmptyMessage="No devices in this group."
          customStyles={{
            container: { flex: 1, minHeight: 0, backgroundColor: 'transparent' },
            header: { padding: compactTable ? '6px' : '8px' },
            cell: {
              padding: compactTable ? '6px' : '8px',
              verticalAlign: 'middle',
              whiteSpace: 'nowrap',
            },
          }}
          muiTableContainerProps={
            compactTable
              ? {
                  sx: {
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    '& table': { tableLayout: 'fixed', width: '100%' },
                  },
                }
              : undefined
          }
          muiTablePaperProps={{
            sx: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
          }}
        />

        {playing != null && errMsg?.[playing] && (
          <div className={styles.error}>{errMsg[playing]}</div>
        )}
        <audio ref={audioRef} className={styles.audio} autoPlay />
      </div>
    );
  }

  return (
    <div
      className="d-flex flex-column rounded-3 bg-primary p-2"
      style={{ height: '100%', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
    >
      <div className="fw-semibold px-2 pb-2">Devices in Group</div>
      <div className="row g-0 px-2 pb-1 border-bottom border-light-subtle fw-semibold">
        <div className="col-6 text-truncate">Device Name</div>
        <div className="col-2 text-center">Node ID</div>
        <div className="col-1" />
        <div className="col-3 text-end">Status</div>
      </div>

      {devices.map((device) => (
        <div
          key={device.id ?? device.node_id ?? device.alias}
          className="row g-0 align-items-center px-2 py-2 border-bottom border-light-subtle"
          onClick={() => onSelect(device.id)}
        >
          <div className="col-6 text-truncate">{device.alias}</div>
          <div className="col-2 text-center">{device.node_id}</div>
          <div className="col-1 d-flex justify-content-center">
            {canDeviceListenLive(device, hideUnavailableLiveAudio) && playing !== device.id && (
              <button
                type="button"
                className={styles['sound-button']}
                onClick={(e) => onListen(e, device.id)}
                aria-label={`Listen to ${device.alias || device.name || 'device'}`}
              >
                <IoVolumeMute size={20} />
              </button>
            )}
            {canDeviceListenLive(device, hideUnavailableLiveAudio) && playing === device.id && (
              <button
                type="button"
                className={styles['sound-button']}
                onClick={onStopListen}
                aria-label={`Stop listening to ${device.alias || device.name || 'device'}`}
              >
                <IoVolumeHigh size={20} />
              </button>
            )}
          </div>
          <div className="col-3 d-flex justify-content-end">
            <NodeStatus device={device} />
          </div>

          {errMsg?.[device.id] && <div className={styles.error}>{errMsg[device.id]}</div>}
        </div>
      ))}

      <audio ref={audioRef} className={styles.audio} autoPlay />
    </div>
  );
}
